
import { handleAxiosException } from '@/api'
import { saleApi, SalesWithSumRegistrationsDto } from '@/api/sales'
import { MinimalTechnicalSaleWithSubscriptionsDto } from '@/api/auth/myHorseRegistration'
import { getTechnicalSaleSubscriptionTypeLabel } from '@/utils/technicalSaleSubscriptions'
import { formatBetween, formatDate, formatCountdown } from '@/utils/dates'
import { addDays, differenceInDays, formatISO, parseISO, startOfDay } from 'date-fns'
import Vue from 'vue'

const bannerSize = {
  'xl': '360',
  'lg': '320',
  'md': '280',
  'sm': '240',
  'xs': '200',
}

export default Vue.extend({
  name: 'Home',

  components: {},
  data() {
    return {
      sales: [],
      agenda: [] as SalesWithSumRegistrationsDto[],
      loading: false,
      error: '',
      selectedSaleIndex: undefined as number | undefined,
      countdown: '',
    }
  },
  methods: {
    async load() {
      this.loading = true

      try {
        this.agenda = await saleApi.getAgenda()
        this.refreshCountdown()
      } catch (e) {
        this.error = handleAxiosException(e)
      }

      this.loading = false
    },
    refreshCountdown() {
      if (this.currentSales.length === 0 && this.futureSales.length > 0) {
        this.countdown = this.formatCountdown(this.futureSales.reduce((acc, x) => (new Date(acc) <= new Date(x.subscribeStart) ? acc : x.subscribeStart), '') ?? '')
        setTimeout(this.refreshCountdown, 1000)
      }
    },
    formatBetween,
    formatDate,
    formatCountdown,
    getTechnicalSaleSubscriptionTypeLabel,
    isRegistrationOpen(sale: MinimalTechnicalSaleWithSubscriptionsDto): boolean {
      const now = startOfDay(new Date())

      if (sale.registrationTicket?.id) {
        return sale.registrationTicket.numberOfRegistrationsUsed < sale.registrationTicket.maxNumberOfRegistrations
      } else if (sale.subscriptions && sale.subscriptions.length > 0) {
        return sale.subscriptions.some(subscription => {
          const startDate = startOfDay(new Date(subscription.startDate))
          const endDate = startOfDay(new Date(subscription.endDate))
          return now >= startDate && now <= endDate
        })
      } else {
        const subscribeStart = startOfDay(new Date(sale.subscribeStart))
        const subscribeEnd = startOfDay(new Date(sale.subscribeEnd))
        return now >= subscribeStart && now <= subscribeEnd
      }
    },
  },
  computed: {
    bannerHeight() {
      return bannerSize[this.$vuetify.breakpoint.name]
    },
    memo(): (SalesWithSumRegistrationsDto & { daysRemaining: { subscribeEnd: number;externalWritersPedigreeStart: number; externalWritersPedigreeEnd: number; startDate: number; documentStart: number } })[] {
      const { now } = this
      return this.agenda.filter(it => it.accepted > 0).map(el => ({
        ...el,
        daysRemaining: {
          subscribeEnd: differenceInDays(parseISO(el.sale.subscribeEnd), now),
          externalWritersPedigreeStart: differenceInDays(parseISO(el.sale.externalWritersPedigreeStart), now),
          externalWritersPedigreeEnd: differenceInDays(parseISO(el.sale.externalWritersPedigreeEnd), now),
          documentStart: differenceInDays(addDays(parseISO(el.sale.startDate), -30), now),
          startDate: differenceInDays(parseISO(el.sale.startDate), now) - 1,
        },
      }))
    },
    displayMemoAlert(): boolean {
      return this.memo.length > 0
        && this.memo.findIndex(e => this.nowFormatted <= e.sale.subscribeEnd
          || e.daysRemaining.documentStart <= 0 && this.nowFormatted <= e.sale.startDate) !== -1
    },
    now(): Date {
      return addDays(new Date(), 0)
    },
    nowFormatted(): string {
      return formatISO(this.now).split('T')[0]
    },
    currentAndFutureSales(): MinimalTechnicalSaleWithSubscriptionsDto[] {
      return this.agenda.map(e => e.sale)
    },
    currentSales(): MinimalTechnicalSaleWithSubscriptionsDto[] {
      return this.agenda.map(e => e.sale).filter(e => new Date(e.subscribeStart) <= this.now)
    },
    futureSales(): MinimalTechnicalSaleWithSubscriptionsDto[] {
      return this.agenda.map(e => e.sale).filter(e => new Date(e.subscribeStart) > this.now)
    },
  },
  mounted() {
    this.load()
  },
})
